<template>
  <el-cascader
    v-model="selected"
    ref="jobSelectorRef"
    filterable
    clearable
    class="job-selector"
    size="small"
    popper-class="job-selector-cascader-popper"
    :show-all-levels="false"
    :props="cascaderProps"
    :placeholder="$t(`${i18nPrefix}.placeholder.select`)"
    :before-filter="handleBeforeFilter"
    :filter-method="handleFilter"
  >
    <template #default="{ node, data }">
      <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="data.i18nName">
        <span>{{ data.i18nName }}</span>
      </el-tooltip>
      <span v-if="!node.isLeaf"> ({{ data.job_list.length }}) </span>
    </template>
  </el-cascader>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'

export default {
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      required: true
    },
    companyIds: {
      required: false
    },
    showSelectAll: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      loader: null,
      selected1: null,
      companyJobList: [],
      matchedFilterNodes: [],
      selectAllOptionEl: null
    }
  },
  computed: {
    cascaderProps () {
      return {
        multiple: true,
        value: 'id',
        label: 'i18nName',
        children: 'job_list',
        lazy: true,
        lazyLoad: this.lazyLoad
      }
    },
    selected: {
      get () {
        const companyJobIds = []
        const jobIds = _.map(this.value, 'id')
        _.forEach(this.companyJobList, com => {
          if (com.type === 'MY_JOB' && jobIds.includes(com.id)) {
            companyJobIds.push([com.id])
          } else {
            _.forEach(com.job_list, job => {
              if (jobIds.includes(job.id)) {
                companyJobIds.push([com.id, job.id])
              }
            })
          }
        })
        return companyJobIds
      },
      set (companyJobIds) {
        const jobIds = []
        _.forEach(companyJobIds, comJobIds => {
          if (comJobIds.includes('MY_JOB')) {
            jobIds.push('MY_JOB')
          } else {
            _.forEach(comJobIds, jobId => {
              const isCompany = _.some(this.companyJobList, { id: jobId })
              if (!isCompany) {
                jobIds.push(jobId)
              }
            })
          }
        })
        const jobList = []
        _.forEach(this.companyJobList, com => {
          if (com.type === 'MY_JOB' && jobIds.includes(com.id)) {
            jobList.push(com)
          }
          _.forEach(com.job_list, job => {
            if (jobIds.includes(job.id)) {
              jobList.push(job)
            }
          })
        })
        this.$emit('change', jobList)
      }
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('company', ['getCompanyGroupedJobList']),
    handleBeforeFilter () {
      this.matchedFilterNodes = []
      return true
    },
    handleFilter (node, keywords) {
      const isMatched = _.includes(node.text.toUpperCase(), keywords.toUpperCase())
      if (isMatched && (node.isLeaf || node.value === 'MY_JOB')) {
        this.matchedFilterNodes.push(node)
      }
      this.refreshSelectAllOption()
      return isMatched
    },
    initData () {
      this.loader = this.loadCompanyJobList()
      if (this.$refs.jobSelectorRef) {
        this.$refs.jobSelectorRef.panel.lazyLoad()
      }
      return this.loader
    },
    async lazyLoad (node, resolve) {
      const level = node.level
      if (level === 0 || node.root) {
        await this.loader
        resolve(this.companyJobList)
      } else {
        const company = _.find(this.companyJobList, { id: node.id })
        resolve((company && company.job_list) || [])
      }
    },
    async loadCompanyJobList () {
      try {
        const resp = await this.getCompanyGroupedJobList({ company_ids: this.companyIds })
        if (resp) {
          const companyJobList = _.map(resp, company => {
            company.cn_name = `${company.id} - ${company.cn_name}`
            company.name = `${company.id} - ${company.name}`
            addI18nProperty(company, 'i18nName', 'cn_name', 'name')
            company.job_list = _.map(company.job_list, job => {
              job.cn_title = `${job.id} - ${job.cn_title}`
              job.title = `${job.id} - ${job.title}`
              job.leaf = true
              addI18nProperty(job, 'i18nName', 'cn_title', 'title')
              return job
            })
            return company
          })
          companyJobList.splice(0, 0, createI18nProperty({
            id: 'MY_JOB',
            type: 'MY_JOB',
            leaf: true
          }, 'i18nName', `${this.i18nPrefix}.label.myJob`))
          this.companyJobList = companyJobList
        }
      } finally {
        this.$eventBus.$emit('jobSelectorLoaded')
      }
    },
    refreshSelectAllOption () {
      if (!this.showSelectAll) {
        return
      }
      const suggestionListEl = this.getSuggestionListEl()
      const shouldShowSelectAll = this.matchedFilterNodes.length > 0 && suggestionListEl
      if (!shouldShowSelectAll) {
        this.hiddenSelectAllOption()
      } else {
        let selectAllOptionEl = this.getSelectAllOptionEl()
        if (!selectAllOptionEl) {
          selectAllOptionEl = document.createElement('li')
          selectAllOptionEl.className = 'el-cascader__suggestion-item job-selector-select-all'
          selectAllOptionEl.textContent = this.$t('common.label.selectAll')
          selectAllOptionEl.setAttribute('tabindex', '-1')
          selectAllOptionEl.addEventListener('click', this.handleSelectAllClick)
          suggestionListEl.prepend(selectAllOptionEl)
        }
        selectAllOptionEl.style.display = 'flex'
      }
    },
    getSuggestionListEl () {
      const cascaderRef = this.$refs.jobSelectorRef
      const suggestionPanelEl = cascaderRef && cascaderRef.$refs && cascaderRef.$refs.suggestionPanel && cascaderRef.$refs.suggestionPanel.$el
      return suggestionPanelEl ? suggestionPanelEl.querySelector('.el-cascader__suggestion-list') : null
    },
    getSelectAllOptionEl () {
      const selectAllOptionEl = document.querySelector('.el-cascader__suggestion-item.job-selector-select-all')
      return selectAllOptionEl
    },
    handleSelectAllClick () {
      const cascaderRef = this.$refs.jobSelectorRef
      if (!cascaderRef || this.matchedFilterNodes.length < 1) {
        return
      }
      _.forEach(this.matchedFilterNodes, node => {
        if (!node.checked) {
          node.doCheck(true)
        }
      })
      cascaderRef.panel.calculateMultiCheckedValue()
    },
    hiddenSelectAllOption () {
      const selectAllOptionEl = this.getSelectAllOptionEl()
      if (!selectAllOptionEl) {
        return
      }
      selectAllOptionEl.style.display = 'none'
    },
    selectMyJob () {
      this.selected = [['MY_JOB']]
    },
    clearSelect () {
      this.selected = []
    }
  },
  watch: {
    companyIds: {
      handler () {
        this.initData()
      }
    },
    '$i18n.locale': {
      handler () {
        const selectAllOptionEl = this.getSelectAllOptionEl()
        if (!selectAllOptionEl) {
          return
        }
        selectAllOptionEl.textContent = this.$t('common.label.selectAll')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-selector {
  width: 100%;

  ::v-deep {
    .el-input.el-input--suffix {
      .el-input__suffix {
        .el-input__icon {
          &.el-icon-arrow-down {
            line-height: 36px;
          }

          &.el-icon-circle-close {
            line-height: 36px;
          }
        }
      }
    }

    .el-cascader__tags {
      align-items: center;

      .el-cascader__search-input {
        font-size: 14px;
      }
    }
  }
}
</style>

<style lang="scss">
.job-selector-cascader-popper {
  .job-selector-select-all {
    z-index: 1;
    font-weight: 500;
    background: #fff;
    border-bottom: 1px solid #ebeef5;
  }

  .el-cascader-menu {
    &:first-child {
      max-width: 300px;
    }
    max-width: 450px;
  }
}
</style>
